import { useState, useContext, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
    Card,
    CardBody,
    CardTitle,
    Label,
    Button,
    ButtonGroup,
} from 'reactstrap';
import { SelectInput } from '~/shared/components/form/Inputs';
import { FundRequestContext } from '~/context/fundrequests';
import { PaymentOptions } from '~/enums/PaymentOptions.enum';
import { getDashColor, IconSwap } from '~/shared/components/icons';
import { Vendor } from '~/types/fundrequests';
import { AddAddressModal } from '../../../Payees/AddAddressModal';

const checkValueExists = (value: any) => {
    if (value) return value;

    return '';
};

const paymentOptionsArray = [
    { label: 'Check', value: PaymentOptions.CHECK },
    { label: 'Direct Deposit (ACH)', value: PaymentOptions.ACH },
];

function PayeeDetails({
    handleSwapPayee,
    loadingPayee,
    initialLoadComplete = true,
}: {
    handleSwapPayee: () => void;
    loadingPayee?: boolean;
    initialLoadComplete?: boolean;
}) {
    const { watch, control, setValue } = useFormContext();
    const [{ selectedPayee, fundRequest }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [openAddressModal, setOpenAddressModal] = useState(false);
    const previousAddressCount = useRef(0);
    const history = useHistory();

    // Sort addresses to put primary first
    const sortedAddresses = selectedPayee?.vendor_addresses
        ? [...selectedPayee.vendor_addresses].sort((a, b) => {
            // Primary addresses first
            if (a.is_primary && !b.is_primary) return -1;
            if (!a.is_primary && b.is_primary) return 1;
            // Then sort by label
            return (a.address_label || '').localeCompare(
                b.address_label || ''
            );
        })
        : [];

    // Handle default vendor address and payment type based on requirements
    useEffect(() => {
        if (selectedPayee) {
            // Handle address selection
            if (selectedPayee?.vendor_addresses?.length && !watch('vendor_address')) {
                let defaultAddressId = null;

                // First priority: Use fundRequest.vendor_address if it exists and is valid
                if (fundRequest?.vendor_address?.id) {
                    const addressExists = selectedPayee.vendor_addresses.some(
                        (address: any) =>
                            address.id === fundRequest.vendor_address.id
                    );

                    if (addressExists) {
                        defaultAddressId = fundRequest.vendor_address.id;
                    }
                }

                // Second priority: Use primary address if no fundRequest address or it wasn't found
                if (!defaultAddressId) {
                    const primaryAddress = selectedPayee.vendor_addresses.find(
                        (address: any) => address.is_primary
                    );

                    if (primaryAddress) {
                        defaultAddressId = primaryAddress.id;
                    } else if (selectedPayee.vendor_addresses.length > 0) {
                        // Third priority: Use first address if no primary found
                        defaultAddressId = selectedPayee.vendor_addresses[0].id;
                    }
                }

                // Set the form value if we found a valid address
                if (defaultAddressId) {
                    setValue('vendor_address', defaultAddressId);
                }
            }

            // Handle payment type initialization
            // When editing existing requests, use fundRequest.payment_type
            if (fundRequest?.payment_type) {
                setValue('payment_type', fundRequest.payment_type);
            }
            // Otherwise if there's a payment type preference on the selected payee, use that
            else if (selectedPayee?.payment_type && !fundRequest?.id) {
                setValue('payment_type', selectedPayee.payment_type);
            }
            // Default value of 'CHECK' is already set in form initialization (index.tsx)
        }
    }, [selectedPayee, fundRequest, setValue, watch]);

    const getFormattedAddress = (
        city: string,
        state: string,
        zipcode: string
    ) => {
        return `${city}, ${state} ${zipcode}`;
    };

    const getAddress = (address: any) => {
        return address?.address_1 || address?.address_2 || '';
    };

    const getButtonStyles = (isSelected: boolean) => ({
        backgroundColor: isSelected ? '#01aeb4' : '',
        borderColor: isSelected ? '#01aeb4' : '',
    });

    const selectedPaymentType = watch('payment_type');
    const selectedAddressId = watch('vendor_address');

    const CustomOption = (props: any) => {
        // Add "(Primary)" to label if this is a primary address
        const isPrimary = props.isPrimary ? ' (Primary)' : '';
        return `${props.label}${isPrimary} - ${getAddress(
            props
        )} ${getFormattedAddress(
            checkValueExists(props?.city),
            checkValueExists(props?.state?.abbreviation),
            checkValueExists(props?.zip_code)
        )}`;
    };

    // Handle payment method change
    const onPaymentMethodClick = (option: PaymentOptions) => {
        setValue('payment_type', option);
        
        // Clear ACH info when switching to CHECK
        if (option === PaymentOptions.CHECK) {
            setValue('ach_info', undefined);
        }
    };

    const handleAddressModalClose = (updatedPayee: any) => {
        fundRequestDispatch({
            type: 'set_selected_payee',
            payload: updatedPayee,
        });

        // If we have a new vendor_address in the fundRequest, select it
        if (fundRequest?.vendor_address?.id) {
            const addressExists = updatedPayee.vendor_addresses.some(
                (address: any) => address.id === fundRequest.vendor_address.id
            );

            if (addressExists) {
                setValue('vendor_address', fundRequest.vendor_address.id);
            }
        }

        setOpenAddressModal(false);
    };

    if (loadingPayee && initialLoadComplete) {
        return <div>Loading payee details...</div>;
    }

    // Don't render anything if no payee is selected
    if (!selectedPayee) {
        return null;
    }

    return (
                <>
                    <Card
                        style={{
                            marginBottom: 20,
                            height: '100%',
                            borderWidth: '0',
                            background: getDashColor('supporters'),
                        }}
                    >
                        <CardBody>
                            <div className='d-flex justify-content-between'>
                                <CardTitle tag='h5'>
                                    {selectedPayee?.name}
                                </CardTitle>
                                <div>
                                    <span style={{ display: 'flex' }}>
                                        <IconSwap />
                                        <p
                                            style={{
                                                color: '#01aeb4',
                                                fontWeight: '400',
                                                marginLeft: '10px',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                handleSwapPayee();
                                            }}
                                        >
                                            SWAP PAYEE
                                        </p>
                                    </span>
                                </div>
                            </div>
                            {selectedPayee?.phone && (
                                <p>{selectedPayee?.phone}</p>
                            )}
                            <p>
                                {selectedPayee?.email && (
                                    <a
                                        href={`mailto:${selectedPayee.email}`}
                                        className='text-break'
                                    >
                                        {selectedPayee.email}
                                    </a>
                                )}
                            </p>

                            {sortedAddresses.length === 1 ? (
                                <div className='mb-3'>
                                    <Label style={{ fontSize: '0.8rem' }}>
                                        Address{' '}
                                        <span className='required'>*</span>
                                    </Label>
                                    <div>
                                        {sortedAddresses[0].address_label && (
                                            <div className='fw-bold small'>
                                                {
                                                    sortedAddresses[0]
                                                        .address_label
                                                }
                                                {sortedAddresses[0]
                                                    .is_primary && ' (Primary)'}
                                            </div>
                                        )}
                                        <div>
                                            {getAddress(sortedAddresses[0])}
                                        </div>
                                        <div>
                                            {getFormattedAddress(
                                                checkValueExists(
                                                    sortedAddresses[0]?.city
                                                ),
                                                checkValueExists(
                                                    sortedAddresses[0]?.state
                                                        ?.abbreviation
                                                ),
                                                checkValueExists(
                                                    sortedAddresses[0]?.zip_code
                                                )
                                            )}
                                        </div>
                                        <input
                                            type='hidden'
                                            name='vendor_address'
                                            value={sortedAddresses[0].id}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <SelectInput
                                    label='Address'
                                    control={control}
                                    name='vendor_address'
                                    options={sortedAddresses.map(
                                        (address: any) => ({
                                            value: address.id,
                                            label: address.address_label,
                                            address_1: address.address_1,
                                            address_2: address.address_2,
                                            city: address.city,
                                            state: address.state,
                                            zip_code: address.zip_code,
                                            isPrimary: address.is_primary,
                                        })
                                    )}
                                    formatOptionLabel={function (data) {
                                        return CustomOption(data);
                                    }}
                                    required={true}
                                    ariaReauired={true}
                                />
                            )}

                            <div
                                className='d-inline-block mb-3 text-primary fw-bold small'
                                style={{ cursor: 'pointer' }}
                                onClick={() => setOpenAddressModal(true)}
                            >
                                Add new address
                            </div>

                            <Label style={{ fontSize: '0.8rem' }}>
                                Preferred Payment Method{' '}
                                <span className='required'>*</span>
                            </Label>
                            <ButtonGroup className='d-grid d-sm-flex gap-2'>
                                {paymentOptionsArray.map((option, i) => (
                                    <Button
                                        key={i}
                                        color='outline-dark'
                                        size='sm'
                                        style={{
                                            ...getButtonStyles(
                                                selectedPaymentType ===
                                                option.value
                                            ),
                                            fontSize: '0.8rem',
                                            textTransform: 'none',
                                            padding: '5px 15px',
                                        }}
                                        onClick={() =>
                                            onPaymentMethodClick(option.value)
                                        }
                                    >
                                        {option.label}
                                    </Button>
                                ))}
                            </ButtonGroup>

                            <hr className='mt-3 mb-3' />
                            <div>
                                <strong>Need to change this info?</strong>{' '}
                                Request changes in{' '}
                                <span
                                    className='text-primary'
                                    style={{
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                    }}
                                    onClick={() =>
                                        history.push(
                                            `/payees/details/${selectedPayee?.id}`
                                        )
                                    }
                                >
                                    Payee Details
                                </span>{' '}
                                before creating a fund request.
                            </div>

                            {selectedPayee?.pending_change_request && (
                                <div
                                    className='mt-3 p-3 text-center fw-bold'
                                    style={{
                                        backgroundColor: '#FFF5E6',
                                        borderRadius: '8px',
                                    }}
                                >
                                    A change request for this payee is currently pending.
                                </div>
                            )}
                        </CardBody>
                    </Card>
                    <AddAddressModal
                        isOpen={openAddressModal}
                        toggle={() => setOpenAddressModal(!openAddressModal)}
                        setPayeeDetails={handleAddressModalClose}
                    />
                </>
    );
}

export default PayeeDetails;
